/* eslint-disable no-param-reassign */
import React from 'react';
import has from 'lodash/has';
import get from 'lodash/get';
import { IReactionDisposer, observable, reaction } from 'mobx';
import * as Graphic from '@src/slide/infrastructure/module/independent/graphic';
import { isLogin } from 'utils';
import {
    ICursorManager,
    CursorManagerEventMap,
    CursorManagerEventType,
    ICursorManagerShowEventPayload,
    ICursorManagerHideEventPayload,
    IElementWithCursor,
    ICursorTagsProps,
} from './cursor-manager.interface';
import { SubscribableService } from '@src/slide/application/service/subscribable-service';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { EditEvents } from '@src/slide/domain/slide-app/core/interfaces/event.interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { isGroupModel, isLayoutModel, isMasterModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { GroupUIEdge } from '@src/framework/editor/src/page/index/components/canvas/element-types/group-element/constant';
import { enumerateGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IBaseModel, ISelectedByOtherInfo } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { IElementCursors } from '@src/slide/domain/slide-app/scenes/online/core/interfaces/cursor-manager.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import CursorTags from '@src/slide/application/service/cursor-manager/core/tags';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';

export class CursorManager extends SubscribableService<CursorManagerEventMap> implements ICursorManager {
    // 当前需要更新协同状态的元素
    @observable public elementsWithCursors: IElementWithCursor[] = [];
    public historyManager: IHistoryManager;
    private selectReaction: IReactionDisposer;
    private loadReaction: IReactionDisposer;

    public constructor(private uiDataService: IUiDataService, private selectionService: ISelectionService) {
        super();
        /**
         * 接受协同光标信息
         */
        const cursorUpdateCb = (obj) => {
            // 修订记录的选择不要更新
            if (this.historyManager?.isShowingHistory) {
                return;
            }
            this.updateElementsCursors(obj.isNew);
        };
        SlideDataServices.EventManager.promise()
            .then(event => event.on(EditEvents.CursorInfoUpdate, cursorUpdateCb));
        this.initReactions();
    }

    public initReactions() {
        if (this.selectReaction) {
            this.selectReaction();
        }
        /**
        * 光标信息发送
        */
        this.selectReaction = reaction(() => {
            const array = this.selectionService.selectedElements.selectedArray;
            if (array) {
                return array.slice(0, array.length);
            }
            return [];
        }, (selection) => {
            // 修订记录的选择不要发送
            if (this.historyManager?.isShowingHistory) {
                return;
            }
            this.handleSelectionChange(selection);
        });
        if (this.loadReaction) {
            this.loadReaction();
        }
        /**
         * 加载完成
         */
        this.loadReaction = reaction(
            () => this.uiDataService.presentation.operation.loading,
            (loading) => {
            // 修订记录的选择不要处理
                if (this.historyManager?.isShowingHistory) {
                    return;
                }
                if (!loading) {
                    this.updateElementsCursors(false);
                }
            },
        );
    }

    public renderCursorTags(props: ICursorTagsProps): React.ReactElement {
        return <CursorTags {...props} cursorManager={this} />;
    }

    /**
     * 显示某个元素的光标标签
     * @param element
     */
    public showCursorTagForElement(element: IElementBaseModel) {
        // 自己选中时，就不要显示tag了
        if (element.selected) {
            return;
        }
        const position = this.cursorTagAnchorPosition(element);
        const cursor = element.selectedByOthers
            && element.selectedByOthers.length > 0 ? element.selectedByOthers[0] : null;
        const name = this.getCursorManager()?.getUserName(cursor?.userId ?? '');
        // 有名称的才显示标签
        if (cursor && name) {
            cursor.name = name;
            this.emit(CursorManagerEventType.SHOW, {
                key: this.getSdkIdFromElement(element),
                position,
                cursor,
            });
        }
    }

    /**
     * 隐藏某个元素的光标标签
     * @param element
     */
    public hideCursorTagForElement(element: IElementBaseModel) {
        this.emit(CursorManagerEventType.HIDE, {
            key: this.getSdkIdFromElement(element),
        });
    }

    /**
     * 刷新光标标签，清除当前显示的&显示没有高亮的
     * @param elements
     */
    public refreshCursorTagsForElements(elements: IElementBaseModel[]) {
        const e = elements.reduce((result, element) => {
            if (element.selectedByOthers && element.selectedByOthers.length > 0 && !element.selected) {
                const position = this.cursorTagAnchorPosition(element);
                const cursor = element.selectedByOthers
                    && element.selectedByOthers.length > 0 ? element.selectedByOthers[0] : null;
                const name = this.getCursorManager()?.getUserName(cursor?.userId ?? '');
                // 没有名字的不显示标签
                if (cursor && name) {
                    cursor.name = name;
                    result.show.push({
                        key: this.getSdkIdFromElement(element),
                        position,
                        cursor,
                    });
                }
            } else {
                result.hide.push({
                    key: this.getSdkIdFromElement(element),
                });
            }
            return result;
        }, {
            show: [] as ICursorManagerShowEventPayload[],
            hide: [] as ICursorManagerHideEventPayload[],
        });
        this.emit(CursorManagerEventType.REFRESH, e);
    }

    /**
     * 清空所有光标标签
     */
    public clearCursorTagsForElements(elements?: IElementBaseModel[]) {
        let keys: Array<string | undefined> = [];
        if (elements) {
            keys = elements.map(element => this.getSdkIdFromElement(element));
        }
        this.emit(CursorManagerEventType.CLEAR, {
            keys,
        });
    }

    /**
     * 检查元素是否在 presentation 中
     * @param element
     * @returns {boolean}
     */
    public isElementInSlide(element: IElementBaseModel): boolean {
        let uiModel: IBaseModel = element;
        while (uiModel.parent) {
            const children = this.getPresentationChildrenByUiModel(uiModel);
            // eslint-disable-next-line no-loop-func
            const hasElement = children?.some(ele => ele.key === uiModel.key);
            if (!hasElement) {
                return false;
            }
            uiModel = uiModel.parent;
        }
        return true;
    }

    /**
     * 获得元素的光标tag位置
     * @param element
     */
    private cursorTagAnchorPosition(element: IElementBaseModel) {
        const frame = this.frameOfElement(element);
        // 右上角
        let position = { x: frame.width, y: 0 };
        if (isGroupModel(element) && element.isRootGroup) {
            position.x += GroupUIEdge;
            position.y -= GroupUIEdge;
        }
        position = this.transformPointByElement(position, element);
        enumerateGroup(element, (group) => {
            position = this.transformPointByElement(position, group);
        });
        return position;
    }

    /**
     * 将元素内的点根据元素的变换，转换到父级的坐标系
     * @param point
     * @param element
     */
    private transformPointByElement(point: { x: number, y: number }, element: IElementBaseModel) {
        let newPoint = { ...point };
        const frame = this.frameOfElement(element);
        newPoint.x += frame.left;
        newPoint.y += frame.top;
        const center = Graphic.centerPointOfRect(frame);
        if (center) {
            if (frame?.flipH === 1) {
                newPoint = Graphic.afterFlipH(newPoint, center.x);
            }
            if (frame?.flipV === 1) {
                newPoint = Graphic.afterFlipV(newPoint, center.y);
            }
        }
        if (has(frame, 'rotate') && frame.rotate) {
            newPoint = Graphic.afterRotate(
                newPoint, frame.rotate,
                frame.left + (frame.width / 2),
                frame.top + (frame.height / 2),
            );
        }
        return newPoint;
    }

    /**
     * 获得元素的变换数据
     * @param {IElementBaseModel} element
     */
    private frameOfElement(element: IElementBaseModel) {
        const { operationFrame } = element;
        const { style } = element.props;
        const frame = {
            width: operationFrame ? operationFrame.width : style.width,
            height: operationFrame ? operationFrame.height : style.height,
            left: operationFrame ? operationFrame.left : style.left,
            top: operationFrame ? operationFrame.top : style.top,
            // eslint-disable-next-line no-nested-ternary
            flipH: has(operationFrame, 'flipH') ? operationFrame?.flipH : has(style, 'flipH') ? style.flipH : 0,
            // eslint-disable-next-line no-nested-ternary
            flipV: has(operationFrame, 'flipV') ? operationFrame?.flipV : has(style, 'flipV') ? style.flipV : 0,
            rotate: has(style, 'rotate') ? style.rotate : 0,
        };
        return frame;
    }

    /**
     * 处理展示层选区更新
     * @param {IElementBaseModel[]} selection
     */
    private handleSelectionChange(selection: IElementBaseModel[]) {
        // 未登录不发送光标信息
        if (!isLogin() || !SlideDataServices.PrivilegeManager()?.editable) return;

        const ids = selection.map(item => (item.sdkData as any)?.uniqueID || item.sdkData?.id) as [string];
        // 发送选中的元素id
        this.getCursorManager()?.updateCursor({
            selection: ids,
        });
        // 清除选中元素hover的光标tag
        this.clearCursorTagsForElements(selection);
    }

    /**
     * 更新元素选
     * @param {boolean} isNew
     */
    private updateElementsCursors(isNew = true) {
        // 没有登录的不展示别人的光标
        if (!isLogin()) {
            return;
        }
        // 保护，可能没有当前slide！
        if (!this.selectionService.currentSlide || !this.getCursorManager()) {
            return;
        }
        const cursors = this.getCursorBySlide();
        const currentSlideId = this.selectionService.currentSlide.sdkData?.id;
        const taggedElements: IElementBaseModel[] = [];
        this.clearCursorElement();
        this.update(this.selectionService.currentSlide, cursors, isNew, currentSlideId, taggedElements);

        // 元素已被删除，则删除数组中该元素的协同框信息
        for (let idx = this.elementsWithCursors.length - 1; idx >= 0; idx--) {
            if (!this.isElementInSlide(this.elementsWithCursors[idx].element)) {
                this.elementsWithCursors.splice(idx, 1);
            }
        }

        // 刷新光标tag的展示
        if (taggedElements.length > 0) {
            this.refreshCursorTagsForElements(taggedElements);
        }
    }

    private getPresentationChildrenByUiModel(uiModel: IBaseModel) {
        if (isMasterModel(uiModel)) {
            return (uiModel.parent as any)?.masterList;
        }
        if (isLayoutModel(uiModel)) {
            return (uiModel.parent as any)?.layoutList;
        }
        return uiModel.parent?.children;
    }

    private getCursorBySlide(): Record<string, IElementCursors[keyof IElementCursors]> {
        const cursors = this.getCursorManager()?.elementCursors as IElementCursors;
        const slideIndex = this.selectionService.currentSlideIndex;
        const slideCursor = {};

        Object.keys(cursors).forEach((id) => {
            const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation();
            const shape = presentation?.slideList[slideIndex].getShapeById(id);
            if (shape) {
                slideCursor[id] = cursors[id];
            }
        });

        return slideCursor;
    }

    // eslint-disable-next-line complexity
    private update(
        element: IBaseModel,
        cursors: Record<string, IElementCursors[keyof IElementCursors]>,
        isNew: boolean,
        currentSlideId: string | undefined,
        taggedElements: IBaseModel[],
    ) {
        const id = this.getSdkIdFromElement(element) ?? '';
        const cursor = cursors[id] || null;
        // 将需要更新光标信息的元素添加到数组中
        if (cursor && cursor.length > 0) {
            if (!this.isTheSameCursor(id, cursor)) {
                this.updateCursorValue(id, cursor, element as IElementBaseModel);
                (element as IElementBaseModel).selectedByOthers = cursor;

                let highlightRoot = false;
                let rootGroup: IGroupModel | null = null;
                // 如果有根的组元素，则根的组元素也高亮
                rootGroup = this.getRootGroup(element);
                if (rootGroup) {
                    const rootId = (rootGroup.sdkData as any)?.uniqueID;
                    if (!this.isTheSameCursor(rootId, cursor)) {
                        this.updateCursorValue(rootId, cursor, rootGroup);
                        rootGroup.selectedByOthers = cursor;
                        highlightRoot = true;
                    }
                }
                // eslint-disable-next-line no-undef
                const slideIdFromSdkData = (element.sdkData as any)?.getPage()?.id;
                if (isNew && slideIdFromSdkData === currentSlideId) {
                    taggedElements.push(element);
                    // 如果高亮了根组，则不要显示根组的标签
                    if (highlightRoot) {
                        const index = taggedElements.findIndex(e => e === rootGroup);
                        if (index !== -1) {
                            taggedElements.splice(index, 1);
                        }
                    }
                }
            }
        } else {
            const index = this.elementsWithCursors.findIndex(e => e.id === id);
            if (index !== -1) {
                this.elementsWithCursors.splice(index, 1);
            }
            (element as IElementBaseModel).selectedByOthers = null;
        }
        element.children.forEach(e => this.update(e, cursors, isNew, currentSlideId, taggedElements));
    }

    // 找根组元素
    private getRootGroup(element: IBaseModel) {
        let uiModel = element.parent;
        while (uiModel && !(isGroupModel(uiModel) && uiModel.isRootGroup)) {
            uiModel = uiModel.parent;
        }
        return uiModel;
    }

    /**
     * 清空协同元素
     */
    private clearCursorElement() {
        this.elementsWithCursors = [];
    }

    private updateCursorValue(id: string, cursor: ISelectedByOtherInfo[], element: IElementBaseModel) {
        const index = this.elementsWithCursors.findIndex(e => e.id === id);
        if (index !== -1) {
            this.elementsWithCursors.splice(index, 1);
        }
        this.elementsWithCursors.push({
            id,
            element,
            cursor,
        });
    }

    private isTheSameCursor(id: string, cursor: ISelectedByOtherInfo[]) {
        const index = this.elementsWithCursors.findIndex(e => e.id === id);
        if (index === -1) {
            return false;
        }
        return !(index !== -1 && this.elementsWithCursors[index].cursor !== cursor);
    }

    private getCursorManager() {
        return OnlineEditServices.CursorManager();
    }

    private getSdkIdFromElement(element: IBaseModel): string | undefined {
        return element.sdkData?.id;
    }
}
